import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useIntl } from 'react-intl';
import {
  Button, Card, CardBody, CardFooter, CardHeader,
  Form, FormGroup, FormText, Label,
} from 'reactstrap';

import FlashesStore from 'src/stores/FlashesStore';
import getFullAddressString from 'src/util/address';
import { TYPEAHEAD_MAX_RESULTS } from 'src/util/constants';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

/**
 * AdminPropertyUserAddForm component provides a form interface for adding users to properties.
 * @param {object} props - Component props
 * @param {Function} props.handleAddFunc - Function to call when adding a user to a property
 * @param {boolean} props.processing - Flag indicating if a request is being processed
 * @param {Array} props.properties - List of available properties
 * @param {Array} props.users - List of available users
 * @param {Function} props.loadNextPropertiesFunc Function used to fetch more property items in the
 *  connection in the "forward" direction
 * @param {boolean} props.hasNextProperties Boolean value which indicates if the end of the
 *  property connection has been reached in the "forward" direction
 * @param {boolean} props.isLoadingNextProperties Boolean value which indicates if a pagination
 *  request for the next property items in the connection is currently in flight, including any
 *  incremental data payloads.
 * @param {Function} props.loadNextUsersFunc Function used to fetch more user items in the
 *  connection in the "forward" direction
 * @param {boolean} props.hasNextUsers Boolean value which indicates if the end of the user
 *  connection has been reached in the "forward" direction
 * @param {boolean} props.isLoadingNextUsers Boolean value which indicates if a pagination
 *  request for the next user items in the connection is currently in flight, including any
 *  incremental data payloads.
 * @returns {React.ReactElement} AdminPropertyUserAddForm component
 */
function AdminPropertyUserAddForm({
  handleAddFunc = null,
  processing = false,
  properties = [],
  users = [],
  loadNextPropertiesFunc,
  hasNextProperties,
  isLoadingNextProperties,
  loadNextUsersFunc,
  hasNextUsers,
  isLoadingNextUsers,
}) {
  const intl = useIntl();

  const [property, setProperty] = useState(null);
  const [propertyValid, setPropertyValid] = useState(null);
  const [user, setUser] = useState(null);
  const [userValid, setUserValid] = useState(null);

  /**
   * Validates if the form has valid selections
   * @returns {boolean} True if either property or user is valid
   */
  const isValid = () => (property?.uuid && user?.uuid);

  /**
   * Handles form submission
   * @param {Event} event - Form submit event
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    const mutationName = 'propertyAddUser';
    const processingMsg = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add.admin_property_user_add_form.state.invalid_still_processing', defaultMessage: 'We are still processing your request...' });
    const errorMsg = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add.admin_property_user_add_form.state.invalid_data', defaultMessage: 'Form data not valid. Please see below.' });
    if (processing) {
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    if (!isValid()) {
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    const { uuid: propertyId } = property;
    const { uuid: userId } = user;

    handleAddFunc(propertyId, userId);
  };

  /**
   * Handles property selection change
   * @param {Array} selected - Selected property items
   */
  const handlePropertyChange = (selected) => {
    if (selected.length > 0) {
      setProperty(selected[0]);
      setPropertyValid(true);
    } else {
      setPropertyValid(false);
    }
  };

  /**
   * Handles user selection change
   * @param {Array} selected - Selected user items
   */
  const handleUserChange = (selected) => {
    if (selected.length > 0) {
      setUser(selected[0]);
      setUserValid(true);
    } else {
      setUserValid(false);
    }
  };

  /**
   * Formats property data for the Typeahead component
   * @returns {Array} Formatted property options
   */
  const propertyOptions = () => properties.map((el) => {
    const { uuid, title, address } = el;
    const addr = getFullAddressString(address);
    if (!addr) {
      return { uuid, label: title };
    }

    const label = `${title} (${addr})`;

    return { uuid, label };
  });

  /**
   * Formats user data for the Typeahead component
   * @returns {Array} Formatted user options
   */
  const userOptions = () => users.map((el) => (
    {
      uuid: el.uuid,
      label: displayUserName(intl, el),
    }));

  const propertyOptionsList = propertyOptions();
  const userOptionsList = userOptions();

  /**
   * Loads the next page of data for the property option list.
   * @param {*} event
   * @param {number} shownResults
   * @returns {void}
   */
  const getNextPageProperties = (event, shownResults) => {
    if (!hasNextProperties || isLoadingNextProperties) {
      return;
    }
    loadNextPropertiesFunc(
      50,
      { onComplete: (error) => { logger.log({ error, shownResults }); } },
    );
  };

  /**
   * Loads the next page of data for the user option list.
   * @param {*} event
   * @param {number} shownResults
   * @returns {void}
   */
  const getNextPageUsers = (event, shownResults) => {
    if (!hasNextUsers || isLoadingNextUsers) {
      return;
    }
    loadNextUsersFunc(
      50,
      { onComplete: (error) => { logger.log({ error, shownResults }); } },
    );
  };

  const submissionDisabled = processing || !userValid || !propertyValid;

  const headerForm = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add.admin_property_user_add_form.add_property_user.header', defaultMessage: 'Add Property User' });

  const propertyLabel = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.property_label', defaultMessage: 'Property' });
  const userLabel = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.user_label', defaultMessage: 'User' });

  const paginationLabel = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.load_more_button.state_load_more.label', defaultMessage: 'Display additional results...' });

  const submitLabel = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.submit_label', defaultMessage: 'Add' });
  const emptyPropertyFeedback = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.property_feedback.hint', defaultMessage: 'Property cannot be empty' });
  const emptyUserFeedback = intl.formatMessage({ id: 'admin.admin_property_user.admin_property_user_add_form.user_feedback.hint', defaultMessage: 'User cannot be empty' });

  return (
    <Form onSubmit={handleSubmit} disabled={processing}>
      <Card>
        <CardHeader tag="h2">
          {headerForm}
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="property">{propertyLabel}</Label>
            <Typeahead
              clearButton
              id="property"
              onChange={handlePropertyChange}
              disabled={processing}
              options={propertyOptionsList}
              isValid={propertyValid}
              isInvalid={propertyValid !== null && !propertyValid}
              maxResults={TYPEAHEAD_MAX_RESULTS}
              paginationText={paginationLabel}
              onPaginate={getNextPageProperties}
              paginate
            />
            {propertyValid != null && !propertyValid && (
            <FormText>{emptyPropertyFeedback}</FormText>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="user">{userLabel}</Label>
            <Typeahead
              clearButton
              id="user"
              onChange={handleUserChange}
              disabled={processing}
              options={userOptionsList}
              isValid={userValid}
              isInvalid={userValid !== null && !userValid}
              maxResults={TYPEAHEAD_MAX_RESULTS}
              paginationText={paginationLabel}
              onPaginate={getNextPageUsers}
              paginate
            />
            {userValid != null && !userValid && (
              <FormText>{emptyUserFeedback}</FormText>
            )}
          </FormGroup>
        </CardBody>
        <CardFooter>
          <Button disabled={submissionDisabled}>{submitLabel}</Button>
        </CardFooter>
      </Card>
    </Form>
  );
}

AdminPropertyUserAddForm.propTypes = {
  handleAddFunc: PropTypes.func,
  processing: PropTypes.bool,
  properties: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postcode: PropTypes.string,
    }),
  })),
  users: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
    email: PropTypes.string,
  })),
  loadNextPropertiesFunc: PropTypes.func.isRequired,
  hasNextProperties: PropTypes.bool.isRequired,
  isLoadingNextProperties: PropTypes.bool.isRequired,
  loadNextUsersFunc: PropTypes.func.isRequired,
  hasNextUsers: PropTypes.bool.isRequired,
  isLoadingNextUsers: PropTypes.bool.isRequired,
};

export default AdminPropertyUserAddForm;
